import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import { DEFAULT_DAYS, defaultTimezone } from "./DayOfWeekField";
import { SetupPage, When, Step, Field, Row, Schedule, SequenceField, NotifyStep, SkipLine, openInBuilder, clampInt, Need } from "./SetupFlow";

const CreateListProspector = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState("list"); // list | saved_search
  const [listId, setListId] = useState("");
  const [listName, setListName] = useState("");
  const [savedSearchId, setSavedSearchId] = useState("");
  const [savedSearchName, setSavedSearchName] = useState("");
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [runWhen, setRunWhen] = useState("DAILY"); // DAILY | WEEKLY | MANUAL
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  useEffect(() => {
    agentsData.fetchContactLists();
    agentsData.fetchSavedSearches();
  }, []);

  const sourcePicked = sourceType === "list" ? !!listId : !!savedSearchId;
  const sourceLabel = sourceType === "list" ? (listName || "a list") : (savedSearchName || "a saved search");
  const canSubmit = sourcePicked && !!sequenceId;

  const buildPayload = () => ({
    name: name.trim() || "Prospect from " + sourceLabel,
    sourceType,
    listId: sourceType === "list" ? listId : undefined,
    savedSearchId: sourceType === "saved_search" ? savedSearchId : undefined,
    sequenceId,
    dailyCap: clampInt(dailyCap, 1, null, 25),
    notifyMode,
    timezone,
    ...(runWhen === "MANUAL" ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createListProspector(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const customize = () => openInBuilder(history, "list-prospector", buildPayload(), setSaving);

  return (
    <SetupPage
      title="Hire · Prospect from a list"
      name={{ value: name, onChange: setName, placeholder: "Prospect from " + sourceLabel }}
      bar={{ canSubmit, saving, onCreate: submit, onCustomize: customize, onCancel: () => history.push("/agents"), reason: "Pick a source and a sequence to create it." }}>
      <When icon="clock">
        <Schedule runWhen={runWhen} setRunWhen={setRunWhen} days={days} setDays={setDays} time={time} setTime={setTime} timezone={timezone} />
      </When>
      <Step icon="list" tone="violet" title="Take contacts from">
        <Row>
          <Field>
            <AgentSelect value={sourceType} onChange={setSourceType} options={[
              { value: "list", label: "A contact list" },
              { value: "saved_search", label: "A saved List Builder search" }
            ]} />
          </Field>
          {sourceType === "list" ? (
            <Field>
              <Need ok={!!listId}><AgentSelect searchable placeholder="Pick a contact list" value={listId}
                onChange={v => { const o = (agentsData.contactLists || []).find(x => x.id === v); setListId(v); setListName(o ? o.name : v); }}
                options={(agentsData.contactLists || []).map(l => ({ value: l.id, label: l.count ? `${l.name} · ${l.count}` : l.name }))} /></Need>
            </Field>
          ) : (
            <Field hint={(agentsData.savedSearches || []).length ? null : "None yet. Save one in List Builder first."}>
              <Need ok={!!savedSearchId}><AgentSelect searchable placeholder="Pick a saved search" value={savedSearchId}
                onChange={v => { const o = (agentsData.savedSearches || []).find(x => x.id === v); setSavedSearchId(v); setSavedSearchName(o ? o.label : v); }}
                options={(agentsData.savedSearches || []).map(x => ({ value: x.id, label: x.label }))} /></Need>
            </Field>
          )}
        </Row>
        <Field label="Contacts per run">
          <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
        </Field>
      </Step>
      <Step icon="mail" tone="violet" title="Find their emails" tags={[{ label: "For email sequences" }]} />
      <Step icon="send" tone="amber" title="Add to a sequence">
        <SequenceField id={sequenceId} name={sequenceName} onPick={s => { setSequenceId(s.id); setSequenceName(s.name); }} />
      </Step>
      <NotifyStep value={notifyMode} onChange={setNotifyMode} />
      <SkipLine />
    </SetupPage>
  );
});

export default CreateListProspector;
